import { Button, colors, FormGroup, Typography } from "@material-ui/core";

import Warning from "@material-ui/icons/Warning";
import Autocomplete from "@material-ui/lab/Autocomplete";
import * as Sentry from "@sentry/browser";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useAlert } from "react-alert";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

import { useQueryClient } from "react-query";

import { setCurrentEnvironment } from "./actions/environments";
import useCurrentEnvironment from "./hooks/use-environment";
import useAccountMetadata from "./hooks/useAccountMetadata";
import useFeatureFlags from "./hooks/useFeatureFlag";
import "./MasqueradePage.css";
import FeatureToggle from "./MasqueradePage/FeatureToggle";
import Navigation from "./Navigation";
import {
  delayAsync,
  makeRequest,
  masqueradeAs,
  masqueradeToken,
  masqueradingMinutesLeft,
  stopMasquerading,
} from "./util";

const AutocompleteMasquerade = ({ value, onChange, ...rest }) => {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState([]);
  const [visibleOptions, setVisibleOptions] = React.useState([]);
  const alert = useAlert();

  useEffect(() => {
    if (open) {
      setVisibleOptions(options);
    } else {
      setVisibleOptions([]);
    }
  }, [open, options]);

  useEffect(() => {
    makeRequest("autocomplete_masquerade", { masquerade_value: value })
      .then((response) => {
        // NOTE(bianchi): ostensibly, we could display the AccountUserCount in the dropdown, but it was breaking when I tried that
        setOptions(response.data.map((user) => user.Email));
      })
      .catch((error) => {
        console.log(error);
        Sentry.captureException(error);
        alert.error("Something went wrong");
      });
  }, [value, alert]);

  return (
    <Autocomplete
      id="Value"
      value={value}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      options={visibleOptions}
      // When first navigating to an event, onInputChange seems to be
      // fired even though it shouldn't be. This is causing issues with
      // the event being marked as edited when it shouldn't be.
      onInputChange={(event, value) => event && onChange(value)}
      freeSolo={true}
      disableClearable={true}
      loading={false}
      renderInput={(params) => (
        <div ref={params.InputProps.ref}>
          <input value={value} {...params.inputProps} {...rest} />
        </div>
      )}
    />
  );
};

AutocompleteMasquerade.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

// Assume the role of another account.
// targetHref is the URL to redirect to after masquerading. Can be nil, in which
// case the current page will just be reloaded.
export async function handleStartMasquerading(
  alert,
  dispatch,
  queryClient,
  masqueradeTarget,
  targetHref
) {
  alert.info(`Attempting to masquerade`);
  return makeRequest("attempt_masquerade", {
    masquerade_value: masqueradeTarget,
  })
    .then(async (response) => {
      const { targetAccountId, targetType } = response.data;
      masqueradeAs(masqueradeTarget);
      if (targetType === "ENV_ID") {
        dispatch(setCurrentEnvironment(masqueradeTarget, queryClient));
      }
      alert.success(`You are now masquerading as account ${targetAccountId}`);
      await delayAsync(1000);
      if (targetHref) {
        window.location.href = targetHref;
      } else {
        window.location.reload();
      }
    })
    .catch((error) => {
      alert.error(
        `Failed to masquerade.
         Please double check your provided input`
      );
      console.log(error);
      Sentry.captureException(error);
    });
}

function handleStopMasquerading(alert) {
  stopMasquerading();
  alert.success("You are no longer masquerading");
  setTimeout(() => window.location.reload(), 1000);
}

function MasqueradeComponent() {
  const params = new URLSearchParams(useLocation().search);
  const [masqueradeTarget, setMasqueradeTarget] = useState(
    params.get("account")
  );
  const alert = useAlert();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const whoAmI = masqueradeToken();
  const minutesLeft = masqueradingMinutesLeft();

  return (
    <>
      <p>
        {whoAmI && `You are currently masquerading as ${whoAmI}. `}
        Masquerade sessions last two hours, after which you will be returned to
        your original account.{" "}
        {minutesLeft &&
          `The current masquerade session will expire in ${minutesLeft} minutes.`}
      </p>
      <AutocompleteMasquerade
        id="masquerade-input-field"
        className="fp-input-field"
        placeholder="Email or Account ID or Environment ID"
        value={masqueradeTarget}
        onChange={setMasqueradeTarget}
        data-1p-ignore="true"
      />

      <div id="start-masquerade-button-container">
        <div
          id="start-masquerade-button"
          className="fp-button fp-button-green"
          onClick={() =>
            handleStartMasquerading(
              alert,
              dispatch,
              queryClient,
              masqueradeTarget,
              undefined
            )
          }
        >
          Start Masquerading
        </div>
      </div>

      <div id="stop-masquerade-button-container">
        <div
          id="stop-masquerade-button"
          className="fp-button fp-button-red"
          onClick={() => handleStopMasquerading(alert)}
        >
          Stop Masquerading
        </div>
      </div>
    </>
  );
}

// This is the page where an admin can masquerade as another user.
export default function MasqueradePage() {
  const currentEnvironment = useCurrentEnvironment();
  const alert = useAlert();
  const account = useAccountMetadata();
  const flag = useFeatureFlags();

  if (!currentEnvironment.loaded || !currentEnvironment.project) {
    return <Navigation>Loading</Navigation>;
  }

  const projectName = currentEnvironment.project.name;

  const downloadHipaaAuditLog = (data, accountId) => {
    const element = document.createElement("a");
    element.setAttribute(
      "href",
      "data:text/plain;charset=utf-8," + encodeURIComponent(data)
    );
    element.setAttribute(
      "download",
      `${accountId}-hipaa-allowlist-auditlog.csv`
    );

    element.style.display = "none";
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
  };

  const handleHipaaAuditLogButtonClick = (alert, account) => {
    makeRequest("get_hipaa_allowlist_auditlog", {})
      .then((response) => {
        const csvData = response.data;
        if (!response.data) {
          alert.error(
            "No HIPAA Allowlist changes found, is this a hipaa customer?"
          );
          return;
        }

        const accountId =
          account.projects.length >= 1 ? account.projects[0].accountID : "";
        downloadHipaaAuditLog(csvData, accountId);
      })
      .catch((error) => {
        console.log(error);
        Sentry.captureException(error);
        alert.error("Something went wrong");
      });
  };

  return (
    <Navigation>
      <div id="masquerade-page">
        <div id="masquerade-component">
          <MasqueradeComponent />
        </div>
      </div>
      <div id="feature-toggles">
        <Typography variant="h4">
          {projectName} Project Feature Flags
        </Typography>
        <FormGroup>
          <FeatureToggle
            featureName="sqlTransformations"
            featureLabel={projectName + " project Proteus Databases"}
            featureLevel="project"
            disableIfEnabled={true}
            description={
              <>
                <p>
                  Cannot be undone. Creates the SingleStore databases for each
                  environment in the project. We control this at a project-level
                  because SingleStore disk space is expensive, and this lets us
                  only create databases for the projects that need them.
                </p>
                <p>
                  This will take awhile. If it takes more than 30 seconds, it
                  will hit the time limit and fail. You can wait a moment and
                  then try to toggle the feature again if this happens.
                </p>
                <p>
                  Please note that, in addition to enabling this feature flag,
                  you'll also need to enable the Proteus account-level feature
                  flag.
                </p>
              </>
            }
          />
        </FormGroup>
        <Typography variant="h4">Account-Level Feature Flags</Typography>
        <FormGroup>
          <FeatureToggle
            featureName="objectSources"
            featureLabel="Object Sources"
            featureLevel="account"
            description={
              <p>
                Normally customers can only send events and users to Freshpaint.
                Object Sources allows customers to send arbitrary entities.
              </p>
            }
          />
          <FeatureToggle
            featureName="hipaaV2"
            featureLabel="HIPAA Mode V2"
            featureLevel="account"
            restricted={true}
            description={
              <>
                <p>
                  <Warning />
                  Only Support and Engineering may toggle HIPAA Mode. If you
                  need to demo HIPAA mode features please masquerade into
                  joey+hipaademo@freshpaint.io and use that account as your
                  sandbox. If you do really need to toggle HIPAA Mode for this
                  account please reach out on slack #eng-helpdesk channel.
                </p>
                <p>
                  <Warning />
                  For HIPAA Mode V1 customers, HIPAA Mode V2 is a breaking
                  change. Additional migration of the allowlist configuration is
                  required in order to avoid breaking customer integrations.
                  Contact Joey in #project-hipaa-mode-v2 about enabling HIPAA
                  Mode V2 for an existing customer.
                </p>
                <p>
                  When a destination is in{" "}
                  <a
                    href="https://documentation.freshpaint.io/hipaa-mode"
                    className="text-brand-red underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    HIPAA mode
                  </a>{" "}
                  the user identifiers attached to events are masked through ID
                  Masking, and Enforced Allowlisting is applied to event
                  properties.
                </p>
              </>
            }
          />
          <FeatureToggle
            featureName="hipaa"
            featureLabel="HIPAA Mode V1 (Legacy)"
            featureLevel="account"
            restricted={true}
            description={
              <>
                <p>
                  <Warning />
                  Only Support and Engineering may toggle HIPAA Mode. If you
                  need to demo HIPAA mode features please masquerade into
                  joey+hipaademo@freshpaint.io and use that account as your
                  sandbox. If you do really need to toggle HIPAA Mode for this
                  account please reach out on slack #eng-helpdesk channel.
                </p>
                <p>
                  <Warning />
                  HIPAA Mode V1 is deprecated. All new accounts should use HIPAA
                  Mode V2.
                </p>
                <p>
                  This is a legacy version of HIPAA mode with slightly different
                  behavior.&nbsp;
                  <a
                    href="https://www.notion.so/freshpaintio/HIPAA-Mode-V2-dc6a119e3ac4490f990da33f51387f00?pvs=4#13666d608f9f47a7acdada03fa96b250"
                    className="text-brand-red underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    For more details read this Notion Doc
                  </a>
                </p>
              </>
            }
          />
          <FeatureToggle
            featureName="personas"
            featureLabel="Personas"
            featureLevel="account"
            description="Allows customers to define cohorts to segment their users by arbitrary criteria."
          />
          <FeatureToggle
            featureName="functions"
            featureLabel="Functions"
            featureLevel="account"
            description="Allows customers to write custom code to implement destinations."
          />
          <FeatureToggle
            featureName="sourceFunctions"
            featureLabel="Source Functions"
            featureLevel="account"
            description={
              <>
                <p>
                  <Warning />
                  This feature is not yet production ready.
                </p>
                <p>
                  Allows customers to write custom code to implement a data
                  source.
                </p>
              </>
            }
          />
          <FeatureToggle
            featureName="geo-info"
            featureLabel="Geolocation Information"
            featureLevel="account"
            description={
              <p>
                When geo-info is enabled, events with an ip address ($ip prop)
                will be enriched with $city and $country props (also $region
                when applicable, e.g. US state name). These props are only added
                on the way to Freshpaint&apos;s snowflake event archive because
                they&apos;re intended for use in warehouse destinations.
              </p>
            }
          />
          <FeatureToggle
            featureName="adBlock"
            featureLabel="Adblocker Bypass"
            featureLevel="account"
            disableIfEnabled={true}
            description={
              <>
                <p>
                  Creates a custom API gateway and cloudfront endpoints for the
                  account. Cannot be undone.
                </p>
                <p>
                  Modifies the javascript snippet used to install Freshpaint on
                  websites. When the modified snippet is installed, adblockers
                  will not be able to prevent the website from sending data to
                  Freshpaint.
                </p>
              </>
            }
          />
          <FeatureToggle
            featureName="sqlTransformations"
            featureLabel="Proteus"
            featureLevel="account"
            description={
              <p>
                Allows access to SQL Transformations and SQL Scheduled Event
                Definitions. Please note that, in addition to enabling this
                feature flag, you need to enable the project-level Proteus
                feature flag in order to create the SingleStore databases for
                each project that needs to use Proteus.
              </p>
            }
          />
          <FeatureToggle
            featureName="allowJsDynamicProp"
            featureLabel="Allow JS Dynamic Property Usage"
            featureLevel="account"
            description="Allows JS Dynamic Property Usage for external users"
          />
          <FeatureToggle
            featureName="networkEventDefs"
            featureLabel="Blackhole / Network Event Capture"
            featureLevel="account"
            description={
              <>
                <p>
                  Enables Blackhole, i.e., capturing of network events for the
                  account.
                </p>
              </>
            }
          />
          <FeatureToggle
            featureName="precisionEventsDefaultNotSegmentCompatible"
            featureLabel="Precision Tracked Events are by default NOT Segment Compatible"
            featureLevel="account"
            description={
              <>
                <p>
                  Normally precision tracked events are by default{" "}
                  <i>Segment Compatible</i> (meaning they will be sent to all
                  active destinations). Some customers (e.g. Monster India){" "}
                  {"don't"} want this behavior as it has the potential to
                  pollute their downstream destionation with a lot of junk
                  events. They want to explictly allowlist events before sending
                  them downstream.
                </p>
                <p>
                  Enabling this flag makes sure that newly created precision
                  tracked events are <b>NOT</b> Segment Compatible by default.
                </p>
              </>
            }
          />
          <FeatureToggle
            featureName="displayNewEventLibraryPage"
            featureLabel="See Event Library Page"
            featureLevel="account"
            description="This feature controls whether we display links in the app to the new Event Library Page. You can always access it by navigating directly to /events/library."
          />
          <FeatureToggle
            featureName="experimentalDestinations"
            featureLabel="Experimental Destinations"
            featureLevel="account"
            description="Provide access to experimental destinations which are not publicly available yet."
          />
          <FeatureToggle
            featureName="rbac"
            featureLabel="Enable RBAC"
            featureLevel="account"
            description="Enable RBAC"
          />
          <FeatureToggle
            featureName="chatSupport"
            featureLabel="Enable FreshChat Chat Support"
            featureLevel="account"
            description="Enable chat support in app"
          />
          <FeatureToggle
            featureName="traces"
            featureLabel="Enable Traces"
            featureLevel="account"
            description="Enable Traces"
          />
          <FeatureToggle
            featureName="webTrackerMonitoring"
            featureLabel="Enable Web Tracker Monitoring UI"
            featureLevel="account"
            description={
              <>
                <p>
                  <Warning /> Be sure to complete the onboarding steps in this
                  document before enabling this feature or it may not function
                  correctly:{" "}
                  <a
                    rel="noreferrer noopener"
                    target="_blank"
                    href="https://www.notion.so/freshpaintio/How-to-onboard-a-customer-to-Web-Tracker-Monitoring-db4c7af81a2a42b49c59953757275d31"
                    style={{ color: colors.blue[700] }}
                  >
                    How to onboard a customer to Web Tracker Monitoring
                  </a>
                </p>
                <p>Shows the Web Tracker Monitoring UI in the app.</p>
              </>
            }
          />
          {flag("webTrackerMonitoring") && (
            <FeatureToggle
              featureName="unifiedDestinationsUI"
              featureLabel="Enable unified Destinations UI for Web Tracker Monitoring"
              featureLevel="account"
              description={
                <>
                  <p>
                    <Warning /> This feature is in early development.
                  </p>
                  <p>
                    Shows the Unified Destinations UI in the Web Tracker
                    Manager.
                  </p>
                </>
              }
            />
          )}
          <FeatureToggle
            featureName="webTrackerMonitoringM3"
            featureLabel="Enable M3 Features For Web Tracker Monitoring UI"
            featureLevel="account"
            description={
              <>
                <p>
                  <Warning /> This feature is in early development.
                </p>
                <p>
                  Shows post-M2 features for Web Tracker Monitoring UI, such as
                  Follow-Up Tasks.
                </p>
              </>
            }
          />
          <FeatureToggle
            featureName="webTrackerMonitoringTrackerBlockedUI"
            featureLabel="Enable Web Tracker Monitoring UI for Tracker Blocking"
            featureLevel="account"
            description={
              <>
                <p>
                  <Warning /> This feature is in early development. The UI is
                  for demo purposes only and uses fake data.
                </p>
                <p>
                  Shows the tracker resolution option for blocking a tracker,
                  and supporting UI. These features are in early development as
                  part of Web Tracker Monitoring M2, the Tracker Blocking
                  project.
                </p>
              </>
            }
          />
          <FeatureToggle
            featureName="useUnifiedEntryScript"
            featureLabel="Enable Unified Entry Script Snippet in the Setup Page"
            featureLevel="account"
            description={
              <>
                <p>
                  Shows the new unified entry script snippet in the setup page.
                  This new snippet changes the way the Freshpaint Web SDK is
                  loaded and will support tracker blocking and video rewriting
                  with no code changes.
                </p>
              </>
            }
          />
          <FeatureToggle
            featureName="disableMasqueradeWarningBanner"
            featureLabel="Disable the Masquerade Warning Banner"
            featureLevel="account"
            description={
              <>
                <p>
                  <Warning />
                  Disabling the masquerade warning banner will remove the banner
                  for anyone accessing the account. Be sure to re-enable the
                  banner when it is safe to do so.
                </p>
                <p>
                  Turning this feature flag on has the effect of{" "}
                  <strong>HIDING</strong> the masquerade warning banner. The
                  masquerade warning banner is a persistent UI element that
                  indicates whether you are currently masquerading as a
                  customer, and shows which customer you are masquerading as.
                </p>
              </>
            }
          />
          <FeatureToggle
            featureName="callrailSource"
            featureLabel="CallRail Source"
            featureLevel="account"
            description={
              <p>
                <Warning />
                This is an experimental feature still in development.
              </p>
            }
          />
          <FeatureToggle
            featureName="invocaSource"
            featureLabel="Invoca Source"
            featureLevel="account"
            description={
              <p>
                <Warning />
                This is an experimental feature still in development.
              </p>
            }
          />
        </FormGroup>
        <Button
          className="h-full"
          color="primary"
          onClick={() => {
            handleHipaaAuditLogButtonClick(alert, account);
          }}
          variant="contained"
        >
          View HIPAA Audit Log
        </Button>
      </div>
    </Navigation>
  );
}
